// SafeHardDeletePage — حذف نهائي آمن للخيل (3.4.3). أداة إدارية مخفيّة (لا قائمة).
// تُفتح بالرابط /horse-hard-delete، لمدير النظام فقط، بحواجز عالية: معاينة عدّاد
// التوابع + كتابة اسم الخيل للتأكيد + إقرار النسخة الاحتياطية. الحذف في معاملة بالخادم.
import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { SmartPage, SmartHeader } from '../../components/smart';
import { SMART_COLORS as C } from '../../theme/smartStableTheme';

export default function SafeHardDeletePage() {
  const { t } = useTranslation();
  const [horseId, setHorseId] = useState('');
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [ackBackup, setAckBackup] = useState(false);

  const reset = () => { setImpact(null); setResult(null); setConfirmName(''); setAckBackup(false); setError(''); };

  const loadImpact = async () => {
    const id = Number(horseId);
    if (!Number.isInteger(id) || id <= 0) { setError(t('invalid_horse_id', 'معرّف خيل غير صالح')); return; }
    setLoading(true); reset();
    try {
      const { data } = await api.get(`/admin/horses/${id}/hard-delete-impact`);
      setImpact(data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setLoading(false); }
  };

  const doDelete = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      const { data } = await api.post(`/admin/horses/${impact.horse.horse_id}/hard-delete`, {
        confirm_name: confirmName,
        acknowledge_backup: ackBackup,
      });
      setResult(data);
      setImpact(null);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setBusy(false); }
  };

  const nameMatches = impact && String(confirmName).trim() === String(impact.horse.name || '').trim();
  const canDelete = impact && nameMatches && ackBackup && !busy;

  return (
    <SmartPage>
      <SmartHeader
        title={t('safe_hard_delete', 'حذف نهائي آمن للخيل')}
        subtitle={t('safe_hard_delete_hint', 'أداة إدارية: تحذف الخيل وكل سجلاته نهائيًّا. لا رجعة — خذ نسخة احتياطية أولًا.')}
      />

      <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
        {t('safe_hard_delete_warning', 'هذا الإجراء يحذف الخيل وكل ما يرتبط به (إيواء/عيادة/بيطار/مسابقات/نقل/إنتاج) نهائيًّا ولا يمكن التراجع عنه. لمدير النظام فقط.')}
      </Alert>

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

      {/* البحث عن الخيل */}
      <Card variant="outlined" sx={{ borderColor: C.line, bgcolor: C.card, mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              label={t('horse_id', 'معرّف الخيل (ID)')}
              value={horseId}
              onChange={(e) => setHorseId(e.target.value.replace(/[^0-9]/g, ''))}
              size="small"
              sx={{ width: { xs: '100%', sm: 220 } }}
            />
            <Button onClick={loadImpact} disabled={loading || !horseId} variant="contained" startIcon={loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SearchIcon />}
              sx={{ bgcolor: C.appbar, color: '#fff', fontWeight: 900, '&:hover': { bgcolor: C.appbarHover } }}>
              {t('load_impact', 'عرض الأثر')}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* الأثر + التأكيد */}
      {impact ? (
        <Card variant="outlined" sx={{ borderColor: C.rose, bgcolor: C.card }}>
          <CardContent>
            <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="caption" sx={{ color: C.muted, fontWeight: 800 }}>{t('horse_label', 'الخيل')}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, color: C.text }}>{impact.horse.name || '—'} <Chip size="small" label={`#${impact.horse.horse_id}`} sx={{ ml: 1, bgcolor: C.cardSoft }} /></Typography>
                {impact.horse.rfid_chip_number ? <Typography variant="caption" sx={{ color: C.muted }}>RFID: {impact.horse.rfid_chip_number}</Typography> : null}
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: C.muted, fontWeight: 800 }}>{t('dependent_rows', 'سجلات تابعة ستُحذف')}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 900, color: C.rose }}>{impact.total}</Typography>
              </Box>
            </Stack>

            {impact.dependents.length ? (
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                {impact.dependents.map((d) => (
                  <Stack key={d.table} direction="row" justifyContent="space-between" sx={{ borderBottom: `1px solid ${C.line}`, py: 0.4 }}>
                    <Typography variant="body2" sx={{ color: C.text, fontFamily: 'monospace' }}>{d.table}</Typography>
                    <Chip size="small" label={d.count} sx={{ bgcolor: C.roseSoft, color: C.rose, fontWeight: 800 }} />
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ color: C.muted, mb: 2 }}>{t('no_dependents', 'لا سجلات تابعة — سيُحذف الخيل فقط.')}</Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography sx={{ fontWeight: 900, color: C.text, mb: 1 }}>{t('confirm_deletion', 'تأكيد الحذف')}</Typography>
            <TextField
              label={t('type_horse_name', 'اكتب اسم الخيل للتأكيد')}
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              size="small"
              error={Boolean(confirmName) && !nameMatches}
              helperText={confirmName && !nameMatches ? t('name_mismatch', 'الاسم لا يطابق') : `${t('expected', 'المتوقّع')}: ${impact.horse.name}`}
              fullWidth
              sx={{ mb: 1 }}
            />
            <FormControlLabel
              control={<Checkbox checked={ackBackup} onChange={(e) => setAckBackup(e.target.checked)} />}
              label={t('ack_backup', 'أقرّ بأنني أخذت نسخة احتياطية لقاعدة البيانات')}
            />
            <Box sx={{ mt: 1 }}>
              <Button onClick={doDelete} disabled={!canDelete} variant="contained" startIcon={busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <DeleteForeverIcon />}
                sx={{ bgcolor: C.rose, color: '#fff', fontWeight: 900, '&:hover': { bgcolor: C.rose } }}>
                {t('hard_delete_now', 'حذف نهائي الآن')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : null}

      {/* النتيجة */}
      {result?.ok ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          {t('hard_delete_done', { name: result.horse_name })} — {result.deleted?.reduce((s, x) => s + x.rows, 0) || 0} {t('dependent_rows_removed', 'سجل تابع أُزيل')}.
        </Alert>
      ) : null}
    </SmartPage>
  );
}
